import type { Product } from "../../products";
import { BUNDLE_CHECKOUT_URL, PRICING } from "@/lib/pricing-constants";
import { getProductScreenshots } from "@/lib/utils/get-product-screenshots";

export const readmonkeyProProduct: Product = {
  id: "readmonkey-pro",
  slug: "readmonkey-pro",
  name: "ReadMonkey Pro — Save Articles, Read Offline (Local-First Chrome)",
  metaTitle: "ReadMonkey Pro — Save Articles, Read Offline (Local-First)",
  alternateName: "ReadMonkey Pro",
  shortName: "ReadMonkey",
  url: "https://plugmonkey.xyz/product/readmonkey-pro/",
  chromeStoreRedirect: "/redirect/readmonkey-pro-chrome-store/",
  description:
    "Distraction-free reader with highlights, tags, full-text search & PDF/MD/JSON export. Syncs across Chrome — no servers, no accounts.",
  shortDescription:
    "Save Articles & Read Later with Distraction-Free Reading, Highlighting, and Built-In Chrome Sync",
  image: ["https://plugmonkey.xyz/product/readmonkey-pro/og-image.png"],
  icon: {
    name: "HiBookOpen",
    backgroundClasses: "bg-[#E8620A]",
    color: "#ffffff",
  },
  category: "Software > Browser Extensions > Productivity",
  applicationCategory: "BrowserApplication",
  operatingSystem: "Chrome",
  audienceType: "Researchers, Students, Professionals, Bookworms",
  freeOffer: {
    name: "Free Version",
    description:
      "Save up to 25 articles per week with 5 tags, 2 highlight colors, JSON export, and title + tag search",
    price: "0",
    priceCurrency: "USD",
    url: "https://plugmonkey.xyz/redirect/readmonkey-pro-chrome-store/",
  },
  offers: {
    monthly: {
      name: "Monthly Subscription",
      description: "Pay as you go. Cancel anytime.",
      price: PRICING.MONTHLY,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/4ee24864-1211-4b27-9209-db6ada219171",
    },
    yearly: {
      name: "Yearly Subscription",
      description: `Save ~${PRICING.YEARLY_SAVINGS_PERCENT}% vs monthly billing.`,
      price: PRICING.YEARLY,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/31c9fc9c-1994-427b-b911-74e39eb315cb",
    },
    lifetime: {
      name: "Lifetime License",
      description: "One-time payment. Own it forever.",
      price: PRICING.LIFETIME,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/1708442e-7634-4799-8595-4690288ff987",
      priceValidUntil: "2030-12-31",
      warranty: {
        durationOfWarranty: "P30D",
        warrantyScope: "30-day money-back guarantee",
      },
    },
  },
  bundleOffer: {
    name: "All-Access Bundle",
    description: "Get every extension we build, forever.",
    price: PRICING.BUNDLE_LIFETIME,
    priceCurrency: "USD",
    url: BUNDLE_CHECKOUT_URL,
  },
  aggregateRating: {
    ratingValue: "4.8",
    reviewCount: "0",
    bestRating: "5",
    worstRating: "1",
  },
  keywords:
    "read later chrome extension, save articles chrome, distraction free reader, article saver, read it later, bookmark manager, article highlighter, offline reading, chrome sync reading list, reading list chrome extension",
  additionalProperties: [
    {
      name: "Platform",
      value: "Chrome Extension",
    },
    {
      name: "Version",
      value: "1.3.6",
    },
    {
      name: "License Type",
      value: "Lifetime",
    },
  ],
  version: "1.3.6",
  hero: {
    // Developer-controlled data with HTML spans for styling, same pattern as ProductPageLayout
    headline:
      "<span class='text-[#E8620A]'>ReadMonkey Pro</span> — Save Any Web Page and Read Distraction-Free",
    subheadline:
      "Capture any page with one click, then read it in a clean, distraction-free view with highlighting, tagging, and multi-format export. Syncs across your Chrome browsers — no external servers, no accounts.",
    ctaText: "Install Free",
    badgeText: "v1.3.6 \u2022 Last updated Nov 3, 2026",
  },
  features: [
    {
      title: "One-Click Saving",
      description:
        "Capture any page from the toolbar, popup, or right-click context menu. Articles are saved instantly to your local library with full text and metadata.",
      icon: "HiBookmarkAlt",
    },
    {
      title: "Distraction-Free Reader",
      description:
        "Read saved articles in a clean layout with light, dark, and sepia themes. Adjust fonts, text size, and reading width to match your preference.",
      icon: "HiEye",
    },
    {
      title: "Highlight & Annotate",
      description:
        "Mark important text in 5 colors and attach inline notes. All highlights are aggregated in a dedicated view for quick review and export.",
      icon: "HiPencil",
    },
  ],
  longFormFeatures: [
    {
      title: "Distraction-Free Reader",
      description:
        "Strip away ads, popups, and clutter. Read saved articles in a clean, focused layout with customizable themes and typography. Keyboard shortcuts for everything.",
      bullets: [
        "Light, dark, and sepia reading themes",
        "Adjustable font family, size, and line height",
        "Configurable reading width (narrow, medium, wide)",
        "Keyboard shortcuts for navigation and theme switching",
      ],
    },
    {
      title: "Color Highlighting & Notes",
      description:
        "Highlight text in up to 5 colors and attach inline notes to any passage. All your highlights are collected in a dedicated panel for quick scanning and export.",
      bullets: [
        "5 highlight colors (yellow, blue, green, pink, purple)",
        "Inline notes on any highlighted passage",
        "Highlight aggregation panel for review",
        "Export highlights separately or with the article",
      ],
    },
    {
      title: "Full-Text Search & Side Panel Library",
      description:
        "Browse your saved articles and highlights in Chrome's side panel without leaving your current tab. Pro searches the full text inside every article — find any phrase you remember reading, not just words in the title or tags.",
      bullets: [
        "Full-text search across article bodies (Pro) — find any phrase inside anything you've saved",
        "Title and tag search (Free)",
        "Highlights tab with color filters",
        "Sort by date saved, title, or domain, and filter by tag",
      ],
    },
    {
      title: "Smart Tagging",
      description:
        "Organize your reading list with custom tags. Filter your library by tag to find related articles fast and keep your research organized.",
      bullets: [
        "Create unlimited tags (Pro) or up to 5 (Free)",
        "Tag-based filtering in the side panel",
        "Bulk tag management",
        "Tag suggestions based on article content",
      ],
    },
    {
      title: "Multi-Format Export",
      description:
        "Export articles and highlights in the format you need. Free users get JSON; Pro unlocks Markdown, HTML, and CSV for seamless integration with your workflow.",
      bullets: [
        "JSON export (Free)",
        "Markdown export for Obsidian, Notion, etc. (Pro)",
        "HTML export for web publishing (Pro)",
        "CSV export for spreadsheets and databases (Pro)",
      ],
    },
    {
      title: "Chrome Sync & Privacy",
      description:
        "Your data syncs across all Chrome browsers where you're signed in via Chrome's built-in sync infrastructure. If Chrome Sync is off, data stays local. No PlugMonkey servers involved — ever.",
      bullets: [
        "Syncs articles, highlights, and tags across devices automatically",
        "No PlugMonkey servers or external cloud — uses Chrome's own sync",
        "No account with us required — works with your existing Chrome sign-in",
        "Zero telemetry or tracking — your reading stays private",
      ],
    },
  ],
  faq: [
    {
      question: "How does ReadMonkey Pro work?",
      answer:
        "ReadMonkey Pro is a Chrome extension that lets you save any web page to a local reading library. Click the toolbar icon or right-click on any page to save it. Open the side panel to browse your library, then click any article to read it in a clean, distraction-free view with customizable themes, highlighting, and notes.",
    },
    {
      question: "Is my data private and safe?",
      answer:
        "Yes. ReadMonkey Pro stores your data using Chrome\u2019s built-in storage with sync support. If Chrome Sync is enabled, your articles, highlights, and notes sync across all your Chrome browsers automatically through Google\u2019s infrastructure \u2014 not ours. No data is ever sent to PlugMonkey servers. No account with us is required, and there is zero tracking or telemetry. If Chrome Sync is disabled, everything stays local on your device.",
    },
    {
      question: "What is the difference between the Free and Pro versions?",
      answer:
        "The Free version gives you 25 saves per week, 5 tags, 2 highlight colors (yellow and blue), JSON export, and title + tag search. Pro unlocks unlimited saves, unlimited tags, all 5 highlight colors plus inline notes, Markdown/HTML/CSV export, and full-text search across all saved content.",
    },
    {
      question: "Is the Pro license a subscription or a one-time payment?",
      answer:
        "You have options! We offer flexible Monthly and Yearly subscriptions, or you can choose the Lifetime License for a one-time payment to own it forever.",
    },
    {
      question: "What export formats are supported?",
      answer:
        "Free users can export in JSON format. Pro users unlock Markdown (great for Obsidian, Notion, or any markdown-based tool), HTML (for web publishing or archiving), and CSV (for spreadsheets and databases). You can export individual articles or your entire library.",
    },
    {
      question: "Can I use ReadMonkey Pro on multiple devices?",
      answer:
        "Yes! Your Pro license is tied to your account, so you can use ReadMonkey Pro on any device where you have the extension installed. With Chrome Sync enabled, your reading library, highlights, and tags sync automatically across all your Chrome browsers.",
    },
    {
      question: "What pages can I save?",
      answer:
        "ReadMonkey Pro can save virtually any web page \u2014 articles, blog posts, documentation, research papers, news stories, and more. It extracts the main content and strips away navigation, ads, and distractions. Some pages with heavy dynamic content or paywalls may have limited extraction.",
    },
    {
      question: "Does it work offline?",
      answer:
        "Yes! Once an article is saved, you can read it offline. Your entire reading library, including highlights and notes, is available without an internet connection since a local copy is always cached on your device.",
    },
    {
      question: "Can I get a refund if I'm not satisfied?",
      answer:
        "Yes, we offer a 30-day money-back guarantee. If ReadMonkey Pro isn't working as described, contact our support team within 30 days of purchase — we'll troubleshoot first (most issues resolve in a single reply), and if we can't resolve it, you get a full refund.",
    },
    {
      question: "How is ReadMonkey Pro different from Pocket or Instapaper?",
      answer:
        "Unlike Pocket or Instapaper, ReadMonkey Pro syncs through Chrome\u2019s own infrastructure \u2014 no proprietary cloud that can be shut down, no account with us required, no tracking. Your data travels between your Chrome browsers via Google\u2019s sync, not through any third-party server. It also runs as a lightweight Chrome extension with a built-in side panel, so there\u2019s no separate app to switch to. Plus, you get highlighting with inline notes and multi-format export that integrates with tools like Obsidian and Notion.",
    },
  ],
  relatedResources: [
    {
      category: "best",
      slug: "best-read-later-chrome-extensions",
      title: "Best Read-Later Chrome Extensions 2026",
      description: "ReadMonkey Pro ranked #1 \u2014 independent ranking after Pocket's shutdown.",
    },
    {
      category: "alternative",
      slug: "pocket",
      title: "Pocket Alternative \u2014 Migration Guide",
      description:
        "Pocket shut down July 2025. ReadMonkey Pro is the direct Chrome-native replacement.",
    },
    {
      category: "alternative",
      slug: "feedly",
      title: "Feedly Alternative \u2014 For Saving + Reading (Not Just RSS)",
      description:
        "Feedly aggregates feeds; ReadMonkey Pro is built for the reading experience itself.",
    },
    {
      category: "guide",
      slug: "highlight-and-annotate-web-articles",
      title: "How to Highlight + Annotate Web Articles",
      description: "5-color system + exportable annotations workflow for research-heavy users.",
    },
    {
      category: "guide",
      slug: "migrate-from-pocket-to-readmonkey-pro",
      title: "Migrate From Pocket to ReadMonkey Pro",
      description: "Import existing Pocket library after Mozilla's shutdown.",
    },
    {
      category: "comparison",
      slug: "readmonkey-pro-vs-instapaper",
      title: "ReadMonkey Pro vs Instapaper",
      description: "Chrome-native vs cloud-native \u2014 privacy + cost compared.",
    },
  ],
  testimonials: [
    {
      text: "I save 10-15 papers a week for my literature reviews. The highlighting with inline notes is exactly what I needed \u2014 I can annotate as I read and export all my highlights to Markdown for my Obsidian vault. Replaced three different tools with one extension.",
      author: "Dr. Maya K.",
      role: "Postdoctoral researcher in computational biology",
      rating: 5,
    },
    {
      text: "The distraction-free reader is genuinely good \u2014 sepia theme with a narrow column width makes long reads comfortable. Search could be a bit faster on large libraries (I have 400+ articles), but it works. The fact that nothing leaves my laptop is a big deal for me.",
      author: "Tom R.",
      role: "Law student preparing for the bar exam",
      rating: 4,
    },
    {
      text: "We curate industry news for our weekly team digest. I save articles throughout the week, tag them by topic, then batch-export as CSV on Fridays. Clean workflow, no sign-ups, syncs across my work and home Chrome. Exactly what I wanted.",
      author: "Priya S.",
      role: "Product marketing manager at a fintech startup",
      rating: 5,
    },
  ],
  useCases: [
    {
      title: "Researchers Collecting References",
      description:
        "Save papers, blog posts, and documentation to a local library. Highlight key passages, attach notes, and export everything to Markdown for your reference manager or knowledge base.",
    },
    {
      title: "Students Building Study Material",
      description:
        "Capture lecture notes, course readings, and supplementary articles. Tag by subject, highlight definitions and key concepts, then review your aggregated highlights before exams.",
    },
    {
      title: "Professionals Curating Industry News",
      description:
        "Save relevant articles throughout the week. Tag by topic or project, then export a digest in CSV or Markdown for team sharing or personal review.",
    },
    {
      title: "Content Curators Organizing Reading Lists",
      description:
        "Build organized reading collections with tags and highlights. Export curated lists in multiple formats for newsletters, social media, or personal archives.",
    },
  ],
  ctaSection: {
    title: "Ready to Read Without Distractions?",
    subtitle:
      "Join researchers, students, and professionals who use ReadMonkey Pro to save, highlight, and organize their reading",
  },
  productHunt: {
    slug: "readmonkey-pro-by-plugmonkey",
    id: "readmonkey-pro",
    title: "ReadMonkey Pro",
    rating: "4.8",
    reviewCount: "0",
  },
  demoSection: {
    title: "See ReadMonkey Pro in Action",
    description:
      "Watch how ReadMonkey Pro saves articles and transforms them into a distraction-free reading experience.",
    slides: getProductScreenshots("readmonkey-pro"),
  },
  howTo: {
    name: "How to Use ReadMonkey Pro to Save and Read Articles",
    description:
      "Start saving articles and reading without distractions in just a few simple steps",
    totalTime: "PT2M",
    tool: "Chrome Browser",
    steps: [
      {
        name: "Install the Extension",
        text: "Add ReadMonkey Pro to Chrome with a single click. No registration required.",
        url: "https://plugmonkey.xyz/redirect/readmonkey-pro-chrome-store/",
        position: 1,
      },
      {
        name: "Save Any Page",
        text: "Click the toolbar icon or right-click on any web page to save it to your local reading library instantly.",
        position: 2,
      },
      {
        name: "Browse Your Library",
        text: "Open the side panel to see all your saved articles. Search, sort by date, and filter by tag to find what you need.",
        position: 3,
      },
      {
        name: "Read in Clean Mode",
        text: "Click any article to open it in the distraction-free reader. Highlight text, add notes, and switch between light, dark, or sepia themes.",
        position: 4,
      },
    ],
  },
  homepageCard: {
    backgroundIconName: "HiBookOpen",
    backgroundIconOpacity: "opacity-5",
    backgroundIconTransform: "transform rotate-12",
    backgroundIconColor: "text-[#E8620A]",
    features: [
      { label: "Reading", value: "Distraction-Free" },
      { label: "Highlights", value: "5 Colors" },
      { label: "Cost", value: "Free / Paid" },
    ],
  },
  displayOrder: 8,
  headerOrder: 600,
  pricing: {
    tagline:
      "Save Articles & Read Later with Distraction-Free Reading, Highlighting, and Built-In Chrome Sync",
    freeFeatures: [
      "25 saves per week",
      "5 tags",
      "2 highlight colors (yellow, blue)",
      "JSON export",
      "All themes & fonts",
      "Title + tag search",
    ],
    proFeatures: [
      "Unlimited saves",
      "Unlimited tags",
      "All 5 colors + inline notes",
      "Markdown, HTML, CSV export",
      "Full-text search",
      "Priority customer support",
      "All future updates included",
    ],
    useCase: "Perfect for researchers, students, and professionals who save articles to read later",
  },
  flowData: {
    installation: {
      headline: "You're ready to read without distractions.",
      subheadline: "Here is how to get the most out of ReadMonkey Pro in 60 seconds.",
      guideSteps: [
        {
          title: "Pin the Extension",
          description:
            "Click the puzzle piece icon in your Chrome toolbar and pin ReadMonkey Pro for easy access.",
          image: "pin-extension",
        },
        {
          title: "Save Your First Article",
          description:
            "Right-click any page and select 'Save to ReadMonkey', or click the ReadMonkey toolbar icon and hit 'Save Page'. Either way, the article is captured instantly.",
          image: "save-article",
          screenshot: "screenshot-1.png",
          ctaText: "Find an Article to Save",
          ctaUrl: "https://en.wikipedia.org/wiki/Speed_reading",
        },
        {
          title: "Open Your Reading Library",
          description:
            "Click the side panel icon to browse saved articles. Read in distraction-free mode with light, dark, or sepia themes.",
          image: "open-library",
          screenshot: "screenshot-2.png",
        },
      ],
      primaryCtaText: "Save Your First Article",
      primaryCtaUrl: "https://en.wikipedia.org/wiki/Speed_reading",
      emailCapture: {
        headline: "Get weekly reading productivity tips",
        description:
          "Join readers and researchers receiving weekly tips on knowledge management, reading workflows, and productivity. No fluff, just tactics.",
        buttonText: "Send me the tips",
      },
    },
    uninstallation: {
      headline: "Sorry to see you go.",
      subheadline: "Feedback is a gift. Tell us how we can improve.",
      survey: {
        question: "What was the main reason for uninstalling?",
        options: [
          "It didn't work as expected",
          "I found a better alternative",
          "It's too expensive",
          "I don't need it anymore",
          "Other",
        ],
      },
    },
  },
};
